import { useRef } from "react";
import { saveAs } from "file-saver";
import { AudioLinesIcon, DownloadIcon } from "lucide-react";

import { AudioPlayer, Column, EmptyState, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { dates } from "@/lib/dates";
import { VoiceRecording } from "@/types";

const BYTES_PER_MB = 1024 * 1024;

const formatSize = (bytes: number): string =>
  `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;

export const VoiceRecordingsTable = ({
  recordings,
}: {
  recordings: VoiceRecording[];
}) => {
  /* Render */
  if (recordings.length === 0) {
    return (
      <EmptyState
        description="Calls with the Staffer assistant are archived here once they end."
        Icon={AudioLinesIcon}
        title="No recordings yet"
      />
    );
  }

  return (
    <Column className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[180px]">Staffer</TableHead>
            <TableHead className="w-[180px]">Recorded</TableHead>
            <TableHead className="w-[100px]">Size</TableHead>
            <TableHead>Playback</TableHead>
            <TableHead className="w-[60px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {recordings.map((recording) => (
            <VoiceRecordingRow
              key={recording.sessionId}
              recording={recording}
            />
          ))}
        </TableBody>
      </Table>
    </Column>
  );
};

// Each row owns its own audio element so players don't fight over one ref.
const VoiceRecordingRow = ({ recording }: { recording: VoiceRecording }) => {
  /* React Hooks */
  const audioRef = useRef<HTMLAudioElement>(null);

  /* Handlers */
  const handleDownload = () => {
    saveAs(recording.url, `${recording.sessionId}.${recording.extension}`);
  };

  /* Render */
  return (
    <TableRow>
      <TableCell>
        <Text className="text-slate-900">
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {recording.stafferName || `User ${recording.userId.toString()}`}
        </Text>
      </TableCell>
      <TableCell>
        <Text className="text-slate-500">
          {recording.recordedAt
            ? dates.formatShortDateTime(recording.recordedAt)
            : recording.date}
        </Text>
      </TableCell>
      <TableCell>
        <Text className="text-slate-500">
          {formatSize(recording.sizeBytes)}
        </Text>
      </TableCell>
      <TableCell>
        <AudioPlayer audioRef={audioRef} audioSrc={recording.url} />
      </TableCell>
      <TableCell>
        <Button onClick={handleDownload} size="icon" variant="ghost">
          <DownloadIcon className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};
